import _extends from "@babel/runtime/helpers/extends";
import React, { useMemo } from 'react';
import styled from 'styled-components';
import { VisuallyHidden } from '@react-aria/visually-hidden';
import { useFocusRing } from '@react-aria/focus';
import { useHover } from '@react-aria/interactions';
import { mergeProps, useId } from '@react-aria/utils';
import { CheckboxIcon } from './CheckboxIcon';
import { boulder, thunderBird } from '@parchment/atoms';
import { useValidator } from '../FieldValidation/useValidator';
import ValidatedFieldTooltip from '../ComposedField/ValidatedFieldTooltip';
const requiredValidator = {
  callback: isSelected => !!isSelected,
  message: 'This field is required.'
};
export function CheckboxInternal(_ref) {
  let {
    scale = 'medium',
    ...props
  } = _ref;
  const errorMessagesId = useId();
  const visuallyDisabled = props.isDisabled || props.isReadOnly;
  const validators = useMemo(() => {
    const validators = props.validators || [];
    if (props.isRequired) {
      validators.push(requiredValidator);
    }
    return validators;
  }, [props.validators, props.isRequired]);
  const {
    isValid,
    errorMessages,
    visibleValidationState,
    validatorProps
  } = useValidator({
    value: props.isSelected,
    onValidate: props.onValidate,
    validators
  });
  const {
    isFocused,
    isFocusVisible,
    focusProps: focusRingProps
  } = useFocusRing();
  const {
    isHovered,
    hoverProps
  } = useHover({});
  const inputProps = mergeProps(props.inputProps, focusRingProps, {
    'aria-invalid': !isValid,
    'aria-describedby': errorMessages.length > 0 ? errorMessagesId : undefined
  });
  return /*#__PURE__*/React.createElement(CheckboxLabel, _extends({
    className: props.className,
    color: props.color,
    scale: scale,
    isDisabled: visuallyDisabled
  }, mergeProps(validatorProps, hoverProps)), /*#__PURE__*/React.createElement(IconWrapper, null, /*#__PURE__*/React.createElement(ValidatedFieldTooltip, {
    id: "validatedFieldTooltip",
    valid: visibleValidationState,
    error: errorMessages.length > 0 ? /*#__PURE__*/React.createElement("div", {
      id: errorMessagesId
    }, errorMessages) : null,
    scale: scale
  }, /*#__PURE__*/React.createElement(VisuallyHidden, null, /*#__PURE__*/React.createElement("input", _extends({}, inputProps, {
    ref: props.inputRef
  }))), /*#__PURE__*/React.createElement(CheckboxIcon, {
    color: visibleValidationState === 'error' ? thunderBird : props.color,
    scale: scale,
    isSelected: props.isSelected,
    isDisabled: props.isDisabled,
    isReadOnly: props.isReadOnly,
    isIndeterminate: props.isIndeterminate,
    isFocused: isFocused,
    isFocusedVisible: isFocusVisible,
    isHovered: isHovered
  }))), /*#__PURE__*/React.createElement("span", null, props.children));
}
const CheckboxLabel = /*#__PURE__*/styled.label.withConfig({
  displayName: "CheckboxInternal__CheckboxLabel",
  componentId: "sc-6lctlc-0"
})(["display:flex;align-items:center;font-size:", ";font-weight:normal;color:", ";cursor:", ";font-style:", ";"], _ref2 => {
  let {
    scale
  } = _ref2;
  return scale === 'small' ? '13px' : '16px';
}, _ref3 => {
  let {
    isDisabled,
    color
  } = _ref3;
  return isDisabled ? boulder : color;
}, _ref4 => {
  let {
    isDisabled
  } = _ref4;
  return isDisabled ? 'not-allowed' : 'default';
}, _ref5 => {
  let {
    isDisabled
  } = _ref5;
  return isDisabled ? 'italic' : 'inherit';
});
const IconWrapper = /*#__PURE__*/styled.span.withConfig({
  displayName: "CheckboxInternal__IconWrapper",
  componentId: "sc-6lctlc-1"
})(["display:inline-flex;margin-right:0.5em;span{display:flex;}"]);